import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Alert, Badge, Button, Card, EmptyState, Spinner, cx, useToast } from '../../components/ui'
import { ApiError, api } from '../../lib/api'
import { date, datetime, money, num, plural } from '../../lib/format'
import type { Checkout, CreditBundle, Payment, Plan, QuotaStatus } from '../../lib/types'

interface SubscriptionResponse {
  plan: Plan
  quota: QuotaStatus
  subscription: {
    status: string
    currentPeriodStart: string
    currentPeriodEnd: string
    cancelAtPeriodEnd: boolean
  } | null
}

/**
 * Providers differ: LiqPay/WayForPay need an auto-submitted POST form, the local
 * stub is a plain redirect. Both are handled here so no page knows PSP details.
 */
function goToCheckout(checkout: Checkout) {
  if (checkout.method === 'GET') {
    window.location.href = checkout.actionUrl
    return
  }
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = checkout.actionUrl
  form.style.display = 'none'
  for (const [name, value] of Object.entries(checkout.fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

export function Billing() {
  const qc = useQueryClient()
  const { push: toast } = useToast()
  const [params, setParams] = useSearchParams()

  const { data: current, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<SubscriptionResponse>('/billing/subscription'),
  })
  const { data: plans } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<Plan[]>('/billing/plans'),
  })
  const { data: bundles } = useQuery({
    queryKey: ['bundles'],
    queryFn: () => api.get<{ bundles: CreditBundle[] }>('/billing/credit-bundles'),
  })
  const { data: payments } = useQuery({
    queryKey: ['payments'],
    queryFn: () => api.get<Payment[]>('/billing/payments'),
  })

  // The PSP sends the user back with ?status=success; confirm and clean the URL.
  useEffect(() => {
    if (params.get('status') === 'success') {
      toast('Оплату отримано — тариф оновлено', 'success')
      void qc.invalidateQueries()
      params.delete('status')
      params.delete('payment')
      setParams(params, { replace: true })
    }
  }, [params, setParams, qc, toast])

  const checkout = useMutation({
    mutationFn: (body: { planCode?: string; credits?: number }) =>
      api.post<Checkout>('/billing/checkout', body),
    onSuccess: goToCheckout,
    onError: (err) =>
      toast(err instanceof ApiError ? err.message : 'Не вдалося створити платіж', 'danger'),
  })

  const cancel = useMutation({
    mutationFn: () => api.post<{ activeUntil: string }>('/billing/subscription/cancel'),
    onSuccess: (res) => {
      toast(`Підписка діятиме до ${date(res.activeUntil)}`, 'success')
      void qc.invalidateQueries({ queryKey: ['subscription'] })
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Помилка', 'danger'),
  })

  if (isLoading || !current) {
    return (
      <div className="grid place-items-center py-24">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    )
  }

  const { quota, plan, subscription } = current

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Тариф і оплата</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Спершу витрачається місячна квота тарифу, потім передплачені кредити.
        </p>
      </header>

      {/* --------------------------------------------------- current plan */}
      <Card
        title={`Поточний тариф — «${plan.name}»`}
        description={
          subscription
            ? `Період: ${date(subscription.currentPeriodStart)} — ${date(subscription.currentPeriodEnd)}`
            : 'Безкоштовний тариф, календарний місяць'
        }
        actions={
          subscription && !subscription.cancelAtPeriodEnd ? (
            <Button
              variant="ghost"
              className="text-xs text-rose-600"
              loading={cancel.isPending}
              onClick={() => {
                if (confirm('Скасувати підписку? Вона діятиме до кінця оплаченого періоду.')) {
                  cancel.mutate()
                }
              }}
            >
              Скасувати
            </Button>
          ) : null
        }
      >
        {subscription?.cancelAtPeriodEnd && (
          <Alert tone="warning" title="Підписку скасовано">
            Тариф діятиме до {date(subscription.currentPeriodEnd)}, потім акаунт
            перейде на безкоштовний план.
          </Alert>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Квота</p>
            <p className="mt-1 text-xl font-bold tabular-nums">
              {quota.unlimited ? '∞' : `${num(quota.used)} / ${num(quota.included)}`}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Кредити</p>
            <p className="mt-1 text-xl font-bold tabular-nums">{num(quota.credits)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Оновлення квоти</p>
            <p className="mt-1 text-xl font-bold">{date(quota.periodEnd)}</p>
          </div>
        </div>
      </Card>

      {/* --------------------------------------------------------- plans */}
      <section>
        <h2 className="mb-4 text-lg font-semibold">Змінити тариф</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {plans?.map((item) => {
            const isCurrent = item.code === plan.code
            const isEnterprise = item.code === 'enterprise'
            return (
              <Card key={item.code} className={cx('flex flex-col', isCurrent && 'ring-2 ring-brand-500')}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold">{item.name}</h3>
                  {isCurrent && <Badge tone="brand">поточний</Badge>}
                </div>
                <p className="mt-2 text-2xl font-bold">
                  {isEnterprise ? 'За запитом' : money(item.priceUah)}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {item.includedRequests < 0
                    ? 'без обмежень'
                    : `${num(item.includedRequests)} запитів / міс`}
                </p>
                <div className="flex-1" />
                <Button
                  variant={isCurrent ? 'secondary' : 'primary'}
                  className="mt-4 w-full text-sm"
                  disabled={isCurrent || isEnterprise || item.priceCents === 0}
                  loading={checkout.isPending}
                  onClick={() => checkout.mutate({ planCode: item.code })}
                >
                  {isCurrent
                    ? 'Активний'
                    : isEnterprise
                      ? 'Звʼязатися'
                      : item.priceCents === 0
                        ? 'Безкоштовний'
                        : 'Обрати'}
                </Button>
              </Card>
            )
          })}
        </div>
      </section>

      {/* ------------------------------------------------------- credits */}
      <section>
        <h2 className="mb-1 text-lg font-semibold">Поповнити кредитами</h2>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Разова оплата без підписки. Кредити не згорають.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {bundles?.bundles.map((bundle) => (
            <Card key={bundle.credits} className="text-center">
              <p className="text-2xl font-bold tabular-nums">{num(bundle.credits)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {plural(bundle.credits, 'кредит', 'кредити', 'кредитів')}
              </p>
              <p className="mt-3 text-lg font-semibold text-brand-600 dark:text-brand-400">
                {money(bundle.priceUah)}
              </p>
              <p className="text-xs text-slate-400">{money(bundle.pricePer1kUah)} за 1000</p>
              <Button
                variant="secondary"
                className="mt-4 w-full text-sm"
                loading={checkout.isPending}
                onClick={() => checkout.mutate({ credits: bundle.credits })}
              >
                Купити
              </Button>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------ payments */}
      <Card title="Історія платежів" padded={false}>
        {!payments?.length ? (
          <EmptyState title="Платежів ще не було" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Дата</th>
                  <th className="px-5 py-2.5 font-medium">Опис</th>
                  <th className="px-5 py-2.5 font-medium">Статус</th>
                  <th className="px-5 py-2.5 text-right font-medium">Сума</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="whitespace-nowrap px-5 py-2.5 text-slate-500 dark:text-slate-400">
                      {datetime(payment.paidAt ?? payment.createdAt)}
                    </td>
                    <td className="px-5 py-2.5">{payment.description}</td>
                    <td className="px-5 py-2.5">
                      <Badge
                        tone={
                          payment.status === 'succeeded'
                            ? 'success'
                            : payment.status === 'pending'
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {
                          {
                            succeeded: 'оплачено',
                            pending: 'очікує',
                            failed: 'помилка',
                            refunded: 'повернено',
                          }[payment.status]
                        }
                      </Badge>
                    </td>
                    <td className="px-5 py-2.5 text-right font-medium tabular-nums">
                      {money(payment.amountUah)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
